import { inject } from '@angular/core';
import { SwPush } from '@angular/service-worker';
import {
  callApiMethod,
  setError,
  setLoaded,
  setLoading,
  withCallState,
} from '@kinet-open-network/client/shared/data-access/state';
import { NotificationChannelPreferences } from '@kinet-open-network/shared/data-access/model';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { firstValueFrom } from 'rxjs';
import { ShellService } from '../services/shell.service';
import { withNotifications } from './notifications.feature';

interface IShellState {
  unreadNotificationsCount: number;
  notificationPreferences: NotificationChannelPreferences;
  pushSupported: boolean;
  pushSubscribed: boolean;
}

export const ShellStore = signalStore(
  { protectedState: false },
  withCallState(),
  withState<IShellState>({
    unreadNotificationsCount: 0,
    notificationPreferences: {},
    pushSupported: false,
    pushSubscribed: false,
  }),
  withMethods(
    (store, service = inject(ShellService), swPush = inject(SwPush)) => ({
      checkPushSubscription: async () => {
        patchState(store, { pushSupported: swPush.isEnabled }, setLoading());

        if (!swPush.isEnabled) {
          patchState(store, setLoaded());
          return;
        }

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        patchState(store, { pushSubscribed: !!subscription }, setLoaded());
      },
      loadPreferences: () => {
        patchState(store, setLoading());

        return callApiMethod(() => service.getNotificationOptions(), {
          next: (preferences) =>
            patchState(
              store,
              { notificationPreferences: preferences },
              setLoaded(),
            ),
          error: (err) => patchState(store, setError(err)),
        });
      },
      updatePreferences: (preferences: NotificationChannelPreferences) => {
        patchState(store, setLoading());

        return callApiMethod(
          () => service.updateNotificationOptions(preferences),
          {
            next: (updatedPreferences) =>
              patchState(
                store,
                {
                  notificationPreferences: updatedPreferences,
                },
                setLoaded(),
              ),
            error: (err) => patchState(store, setError(err)),
          },
        );
      },
      subscribeToPush: async () => {
        if (!swPush.isEnabled) {
          return;
        }

        patchState(store, setLoading());
        try {
          const publicKey = await firstValueFrom(service.getPushPublicKey());
          const subscription = await swPush.requestSubscription({
            serverPublicKey: publicKey,
          });

          await firstValueFrom(service.subscribePush(subscription.toJSON()));
          patchState(store, { pushSubscribed: true }, setLoaded());
        } catch (error) {
          patchState(store, setError(error));
        }
      },
      unsubscribeFromPush: async () => {
        if (!swPush.isEnabled) {
          return;
        }

        patchState(store, setLoading());
        try {
          const subscription = await firstValueFrom(swPush.subscription);

          if (!subscription) {
            patchState(store, { pushSubscribed: false }, setLoaded());
            return;
          }

          await swPush.unsubscribe();
          await firstValueFrom(
            service.unsubscribePush(subscription.toJSON().endpoint as string),
          );
          patchState(store, { pushSubscribed: false }, setLoaded());
        } catch (error) {
          patchState(store, setError(error));
        }
      },
    }),
  ),
  withNotifications(),
  withHooks({
    onInit: (store) => {
      store.loadUnreadNotificationsCount();
      store.checkPushSubscription();
    },
  }),
);
